import type { AdvanceRow, EntryRow } from '../lib/day';
import { formatDate, formatRs, rupeesInWords } from '../lib/money';
import SignatureImage from '../components/SignatureImage';
import StaffStamp from '../components/StaffStamp';

/**
 * The advance receipt.
 *
 * "Figures and words, the pay week it comes off, advances that week, and the
 * captured signature. Two per A4 with a cut line."
 *
 * The words matter: this is the document the man signs to say he took the cash,
 * and a figure alone can be altered afterwards.
 */

/** Who approved that day's labour — "Approved" on the receipt, signed on the screen. */
export interface ReceiptApproval {
  by: string | null | undefined;
  name: string | null | undefined;
  at: string | null | undefined;
}

interface Props {
  entry: EntryRow;
  advance: AdvanceRow;
  workDate: string;
  approval?: ReceiptApproval | undefined;
}

export function Receipt({ entry, advance, workDate, approval }: Props) {
  return (
    <div className="receipt">
      <div className="rechead">
        <div className="rectitle">Advance payment receipt</div>
        <div>{formatDate(workDate)}</div>
      </div>

      <table>
        <tbody>
          <tr>
            <td style={{ width: '40%' }}>Paid to</td>
            <td>
              <b>{entry.workerName}</b>
              {entry.rolesLabel && ` — ${entry.rolesLabel}`}
            </td>
          </tr>
          <tr>
            <td>Amount</td>
            <td>
              <b>{formatRs(advance.amountRs)}</b> (rupees {rupeesInWords(advance.amountRs)} only)
            </td>
          </tr>
          <tr>
            <td>Purpose</td>
            <td>
              Advance against wages, week ending {formatDate(advance.payWeekEnding)}
              {advance.note && ` — ${advance.note}`}
            </td>
          </tr>
          <tr>
            <td>Advances this week</td>
            <td>
              {formatRs(entry.advancesThisWeekRs)} including this one — deducted from the
              weekly pay
            </td>
          </tr>
          <tr>
            <td>Paid by</td>
            <td>{advance.paidByName}</td>
          </tr>
        </tbody>
      </table>

      {advance.signed ? (
        <>
          <div style={{ marginTop: 10, fontSize: 12 }}>Received by (employee):</div>
          <div className="sigprint">
            <SignatureImage advanceId={advance.id} />
          </div>
          <div style={{ fontSize: 11 }}>
            Signed on the tablet,{' '}
            {advance.signedAt ? new Date(advance.signedAt).toLocaleString('en-GB') : ''}
          </div>
          <div className="siglines" style={{ marginTop: 10 }}>
            <StaffStamp label="Approved" userId={approval?.by} name={approval?.name} at={approval?.at} />
          </div>
        </>
      ) : (
        // Nobody signed on the tablet, so the paper has to carry the line.
        <div className="siglines" style={{ marginTop: 22 }}>
          <div>Received by (employee): __________________________</div>
          <StaffStamp label="Approved" userId={approval?.by} name={approval?.name} at={approval?.at} />
        </div>
      )}
    </div>
  );
}

/**
 * Two receipts to a sheet with a cut line between them, so the office keeps one
 * half and the man takes the other.
 */
export function ReceiptSheets({
  items, workDate,
}: {
  items: { entry: EntryRow; advance: AdvanceRow; workDate?: string; approval?: ReceiptApproval | undefined }[];
  /** The date for items that do not carry their own. */
  workDate: string;
}) {
  const sheets: { entry: EntryRow; advance: AdvanceRow; workDate?: string; approval?: ReceiptApproval | undefined }[][] = [];
  for (let i = 0; i < items.length; i += 2) sheets.push(items.slice(i, i + 2));

  return (
    <>
      {sheets.map((sheet, index) => (
        <div key={index} className={index < sheets.length - 1 ? 'pbreak' : undefined}>
          {sheet.map((item, position) => (
            <div key={item.advance.id}>
              <Receipt
                entry={item.entry}
                advance={item.advance}
                workDate={item.workDate ?? workDate}
                approval={item.approval}
              />
              {position === 0 && sheet.length > 1 && <div className="cutline">cut here</div>}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
